import { each, find } from 'lodash-es';
import { Features } from '../../../../../../../../frontend/libs/shared/src/lib/types/features';

export type ProductGridColumns =
  | 'product'
  | 'seller'
  | 'unspsc'
  | 'spend'
  | 'crosses'
  | 'reviewed';

export interface IProduct {
  catalogNumber: string;
  productId: number;
  productDescription: string;
  status: string;
  manufacturer?: {
    id: number;
    name: string;
  };
  crossReferenceCount?: number;
  crossApproveCount?: number;
  crossDisputeCount?: number;
  seller: {
    id: number;
    name: string;
  };
  spend?: number;
  unitsPurchased?: number;
  price?: number;
}

class ProductsGridController {
  static bindings = {
    featureList: '<',
    products: '<',
    gridConfigOverride: '<', // this is optional
    searchTerm: '<',
    visibleColumns: '<',
    isSeller: '<',
    orgId: '<',
    openCrosses: '&',
  };

  orgId: number;

  gridConfigOverride;

  featureList: Features;
  products = [];
  // Data is filtered before being passed in but we need the search term to highlight
  searchTerm;
  visibleColumns;
  spendPopoverMessageForTheseProducts: string =
    'Spend for these products does not count towards total committed target or compliance.';

  // TODO: Add ellipsis component, likely to every column
  gridApi;
  gridConfig = {
    minRowsToShow: 10,
    enableColumnMenus: false,
    enableHorizontalScrollbar: this.uiGridConstants.scrollbars.NEVER,
    rowHeight: 80,
    columnDefs: [
      {
        visibilityName: 'product',
        // <ellipsis content="COL_FIELD"></ellipsis>
        // How will this work with the highlight filter if the highlighted text is cut off?
        displayName: 'Product',
        cellTemplate: `<div class="ui-grid-cell-contents" data-test-selector="product-name">
                         <div style="display: flex;">
                           <discontinued-badge status="row.entity.status"></discontinued-badge>
                           <a ng-href="/aptitude/static/products.html#/view/productID/{{ row.entity.productId }}">
                             <ellipsis content="row.entity.catalogNumber | aptHighlightText:grid.appScope.$ctrl.searchTerm" data-test-selector="catalog-number"></ellipsis>
                           </a>
                          </div>
                         <div style="max-height: 40px; overflow-y: hidden;" multiline-ellipsis="row.entity.productDescription | aptHighlightText:grid.appScope.$ctrl.searchTerm"></div>
                       </div>`,
        field: 'catalogNumber',
        headerCellClass: 'align-left',
        minWidth: 100,
        width: '*',
        sort: {
          direction: this.uiGridConstants.ASC,
          priority: 0,
        },
      },
      {
        visibilityName: 'seller',
        displayName: 'Seller',
        field: 'seller.name',
        cellTemplate: `<div class="ui-grid-cell-contents" data-test-selector="seller-col">
          <ellipsis content="COL_FIELD" data-test-selector="seller-name"></ellipsis>
          <span class="text-muted"
                data-test-selector="manufacturer-name"
                ng-if="COL_FIELD != row.entity.manufacturer.name">
            <ellipsis content="row.entity.manufacturer.name"></ellipsis>
          </span>
        </div>`,
        headerCellClass: 'align-left',
        minWidth: 100,
        width: 200,
      },
      {
        visibilityName: 'unspsc',
        displayName: 'UNSPSC',
        field: 'unspsc.name',
        cellTemplate: `<div class="ui-grid-cell-contents">
          <ellipsis content="COL_FIELD"></ellipsis>
        </div>`,
        headerCellClass: 'align-left',
        minWidth: 100,
        width: '*',
      },
      {
        visibilityName: 'spend',
        displayName: 'Spend',
        field: 'spend',
        headerCellClass: 'text-right',
        cellClass: 'text-right',
        cellFilter: 'nfcurrency',
        cellTemplate: `<div class="ui-grid-cell-contents text-right">
          <span bs-popover
                ng-style="{ color: row.entity.isCarveIn ? 'purple' : '' }"
                data-placement="top"
                data-viewport="{ selector: '.ui-grid-canvas', padding: 2 }"
                data-bs-enabled="row.entity.isCarveIn || (COL_FIELD == 0 || COL_FIELD == null)"
                data-trigger="click"
                data-auto-close="true"
                data-template-url="spendPopoverTemplateForTheseProducts"
                data-test-selector="spend-amount"
                ng-attr-title="{{ (COL_FIELD == 0 || COL_FIELD == null) ? 'Zero spend data found for this product' : undefined || row.entity.isCarveIn ? grid.appScope.$ctrl.spendPopoverMessageForTheseProducts : ''}}">{{ COL_FIELD | nfcurrency }}</span>
          <span bs-popover
                class="muted"
                data-placement="top"
                data-viewport="{ selector: '.ui-grid-canvas', padding: 2 }"
                data-bs-enabled="row.entity.isCarveIn"
                data-trigger="click"
                data-auto-close="true"
                data-template-url="spendPopoverTemplateForTheseProducts"
                title="{{row.entity.isCarveIn ? grid.appScope.$ctrl.spendPopoverMessageForTheseProducts : ''}}"
                ng-style="{ color: row.entity.isCarveIn ? 'purple' : '' }"
                data-test-selector="units-purchased"
                ng-if="grid.appScope.$ctrl.featureList.Add_Units_Purchased_to_Products_By_Category_ADEX_25">{{ row.entity.unitsPurchased | number }} Units</span>
        </div>`,
        type: 'number',
        minWidth: 100,
        width: 100,
      },
      {
        visibilityName: 'price',
        displayName: 'Avg Price',
        field: 'price',
        cellFilter: 'nfcurrency',
        cellTemplate: `<div class="ui-grid-cell-contents">
          <span data-test-selector="average-price"
            bs-tooltip
            data-placement="bottom"
            data-trigger="click"
            data-auto-close="true"
            data-container="section"
            data-title="Weighted avg last reasonable price paid"
            title="Weighted avg last reasonable price paid">
              {{ COL_FIELD | nfcurrency:'$':5 }}{{ COL_FIELD !== null ? '/Unit' : '' }}
            </span>
        </div>`,
        headerCellClass: 'text-right',
        cellClass: 'text-right',
        minWidth: 150,
        width: 150,
      },
      {
        visibilityName: 'crosses',
        displayName: 'Crosses',
        field: 'crossReferenceCount',
        headerCellClass: 'text-center',
        cellTemplate: `<div class="ui-grid-cell-contents text-center crosses-col">
          <button class="btn btn-link"
                  data-test-selector="crosses-count"
                  title="View Crosses"
                  ng-click="grid.appScope.$ctrl.openCrosses({visibleRows: grid.getVisibleRows(), rowIndex: grid.renderContainers.body.visibleRowCache.indexOf(row)})">
              <i class="fas fa-globe-americas" aria-hidden="true"></i> {{ row.entity.crossReferenceCount | number }}
          </button>
        </div>`,
        cellFilter: 'number',
        type: 'number',
        minWidth: 100,
        width: 100,
      },
      {
        visibilityName: 'reviewed',
        displayName: 'Reviewed',
        field: 'crossApproveCount',
        headerCellClass: 'text-center',
        cellTemplate: `<div class="ui-grid-cell-contents text-center reviewed-col">
          <span bs-tooltip
                data-placement="auto bottom"
                container="body"
                data-trigger="click"
                title="The number of crosses you have accepted or rejected for this product">
            <i class="fa fa-thumbs-up" ng-class="{ 'apt-mid-gray': row.entity.crossApproveCount == 0, 'apt-green': row.entity.crossApproveCount > 0 }" aria-hidden="true"></i>
            <span ng-class="{ 'apt-mid-gray': row.entity.crossApproveCount == 0 }">{{ row.entity.crossApproveCount | number }}</span>
            <i class="fa fa-thumbs-down" ng-class="{ 'apt-mid-gray': row.entity.crossDisputeCount == 0, 'apt-orange': row.entity.crossDisputeCount > 0 }" aria-hidden="true"></i>
            <span ng-class="{ 'apt-mid-gray': row.entity.crossDisputeCount == 0 }">{{ row.entity.crossDisputeCount | number }}</span>
          </span>
        </div>`,
        cellFilter: 'number',
        minWidth: 100,
        width: 100,
      },
      {
        visibilityName: 'onContract',
        displayName: '<i class="apt-contracted-icon fa-lg"></i>',
        field: 'onContract',
        headerCellTemplate: this.headerCellTemplate,
        cellTemplate: `<div class="ui-grid-cell-contents">
          <i data-test-selector="contracted-icon" ng-if="COL_FIELD" class="apt-contracted-icon fa-lg"></i>
        </div>`,
        headerCellClass: 'text-center',
        cellClass: 'text-center',
        minWidth: 80,
        width: 80,
      },
      {
        visibilityName: 'score',
        displayName: 'Score',
        field: 'score',
        type: 'number',
        headerCellClass: 'text-center',
        cellClass: 'text-center',
        cellFilter: 'percentage:0',
        minWidth: 100,
        width: 100,
      },
    ],
    // The filtered products
    data: [],
    onRegisterApi: (gridApi) => {
      this.gridApi = gridApi;
      this.GridScrollHistory.registerScrollHistory(
        gridApi,
        this.gridConfig,
        this.$scope,
      );
      this.updateVisibleColumns(this.visibleColumns);
    },
  };

  constructor(
    private uiGridConstants,
    private GridScrollHistory,
    private $scope,
    private headerCellTemplate,
  ) {
    'ngInject';

    // Hide all columns by default and only show the ones specified in visibleColumns
    each(this.gridConfig.columnDefs, (columnDef) => {
      (columnDef as any).visible = false;
    });
  }

  $onChanges(changes) {
    if (changes.products && changes.products.currentValue) {
      // Set it to an empty array if products is undefined (unloaded)
      this.gridConfig.data = changes.products.currentValue || [];
    }

    if (this.gridConfigOverride) {
      this.gridConfig = Object.assign(this.gridConfig, this.gridConfigOverride);
      this.gridApi?.grid.refresh();
    }

    // When the visible columns array changes, update the visible columns
    if (
      changes.visibleColumns &&
      changes.visibleColumns.currentValue &&
      !this.gridConfigOverride
    ) {
      this.updateVisibleColumns(changes.visibleColumns.currentValue);
    }
  }

  // This is used by the grid config in the seller side of Products grid
  getProductUrl(product: IProduct): string {
    const isSellerProduct = this.orgId === product.seller.id;

    return isSellerProduct
      ? `/aptitude/static/products.html#/edit/productID/${product.productId}`
      : `/aptitude/static/products.html#/view/productID/${product.productId}`;
  }

  updateVisibleColumns(columns) {
    if (this.gridApi) {
      each(
        this.gridConfig.columnDefs,
        (column: any) => (column.visible = false),
      );
      each(columns, (column) => {
        let columnDef: any = find(this.gridConfig.columnDefs, {
          visibilityName: column,
        });
        if (columnDef) {
          columnDef.visible = true;
        } else {
          // console.log(`Could not find column named '${column}'`);
        }
      });
      this.gridApi.grid.refresh();
    }
  }
}

export const ProductsGridComponent = {
  bindings: ProductsGridController.bindings,
  controller: ProductsGridController,
  template: `<div class="apt-ui-grid-default grid-with-cell-borders"
               ui-grid="$ctrl.gridConfig"
               ui-grid-auto-resize
               ui-grid-resize-columns
               ui-grid-save-state
               data-test-selector="products-grid"></div>
`,
};
